import React, { useMemo } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type { OrgMemberResponse, ClinicAssignmentRow, RoleFlag } from './types';

// ─── AssignmentRow ───────────────────────────────────────────────────────────

type AssignmentRowProps = {
  row: ClinicAssignmentRow;
  onToggleAssigned: (clinicId: string) => void;
  onToggleFlag: (clinicId: string, flag: RoleFlag) => void;
};

const AssignmentRow: React.FC<AssignmentRowProps> = React.memo(
  function AssignmentRow({ row, onToggleAssigned, onToggleFlag }) {
    return (
      <TableRow className={row.assigned ? '' : 'opacity-50'}>
        <TableCell>
          <Checkbox
            checked={row.assigned}
            onCheckedChange={() => onToggleAssigned(row.clinic_id)}
          />
        </TableCell>
        <TableCell>
          <Label className='font-medium'>{row.clinic_name}</Label>
        </TableCell>
        <TableCell className='text-center'>
          <div className='flex justify-center'>
            <Switch
              checked={row.is_assignable_clinician}
              onCheckedChange={() =>
                onToggleFlag(row.clinic_id, 'is_assignable_clinician')
              }
              disabled={!row.assigned}
            />
          </div>
        </TableCell>
        <TableCell className='text-center'>
          <div className='flex justify-center'>
            <Switch
              checked={row.is_case_manager}
              onCheckedChange={() =>
                onToggleFlag(row.clinic_id, 'is_case_manager')
              }
              disabled={!row.assigned}
            />
          </div>
        </TableCell>
        <TableCell className='text-center'>
          <div className='flex justify-center'>
            <Switch
              checked={row.is_approver}
              onCheckedChange={() => onToggleFlag(row.clinic_id, 'is_approver')}
              disabled={!row.assigned}
            />
          </div>
        </TableCell>
      </TableRow>
    );
  }
);

// ─── EditAssignmentsDialog ───────────────────────────────────────────────────

type EditAssignmentsDialogProps = {
  member: OrgMemberResponse | null;
  assignments: ClinicAssignmentRow[];
  isSaving: boolean;
  onClose: () => void;
  onSave: () => void;
  onToggleAssigned: (clinicId: string) => void;
  onToggleFlag: (clinicId: string, flag: RoleFlag) => void;
  onToggleFlagForAllAssigned: (flag: RoleFlag, value: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
};

export const EditAssignmentsDialog: React.FC<EditAssignmentsDialogProps> =
  React.memo(function EditAssignmentsDialog({
    member,
    assignments,
    isSaving,
    onClose,
    onSave,
    onToggleAssigned,
    onToggleFlag,
    onToggleFlagForAllAssigned,
    onSelectAll,
    onDeselectAll,
  }) {
    const assignedRows = useMemo(
      () => assignments.filter(r => r.assigned),
      [assignments]
    );
    const allAssigned =
      assignments.length > 0 && assignedRows.length === assignments.length;

    // Whether all assigned rows have a given flag checked
    const allAssignedHaveFlag = (flag: RoleFlag) =>
      assignedRows.length > 0 && assignedRows.every(r => r[flag]);

    return (
      <Dialog
        open={!!member}
        onOpenChange={open => {
          if (!open) onClose();
        }}
      >
        <DialogContent className='sm:max-w-[800px] max-h-[85vh] overflow-hidden flex flex-col'>
          <DialogHeader>
            <DialogTitle>
              Manage Clinic Access &mdash; {member?.first_name}{' '}
              {member?.last_name}
            </DialogTitle>
            <DialogDescription>{member?.email}</DialogDescription>
          </DialogHeader>

          <div className='overflow-y-auto flex-1 -mx-6 px-6'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className='w-[50px]'>
                    <Checkbox
                      checked={allAssigned}
                      onCheckedChange={checked =>
                        checked ? onSelectAll() : onDeselectAll()
                      }
                    />
                  </TableHead>
                  <TableHead>Clinic</TableHead>
                  <TableHead className='text-center w-[120px]'>
                    <div className='flex flex-col items-center gap-1'>
                      <span>Clinician</span>
                      <Checkbox
                        checked={allAssignedHaveFlag('is_assignable_clinician')}
                        disabled={assignedRows.length === 0}
                        onCheckedChange={checked =>
                          onToggleFlagForAllAssigned(
                            'is_assignable_clinician',
                            !!checked
                          )
                        }
                      />
                    </div>
                  </TableHead>
                  <TableHead className='text-center w-[120px]'>
                    <div className='flex flex-col items-center gap-1'>
                      <span>Case Manager</span>
                      <Checkbox
                        checked={allAssignedHaveFlag('is_case_manager')}
                        disabled={assignedRows.length === 0}
                        onCheckedChange={checked =>
                          onToggleFlagForAllAssigned(
                            'is_case_manager',
                            !!checked
                          )
                        }
                      />
                    </div>
                  </TableHead>
                  <TableHead className='text-center w-[120px]'>
                    <div className='flex flex-col items-center gap-1'>
                      <span>Approver</span>
                      <Checkbox
                        checked={allAssignedHaveFlag('is_approver')}
                        disabled={assignedRows.length === 0}
                        onCheckedChange={checked =>
                          onToggleFlagForAllAssigned('is_approver', !!checked)
                        }
                      />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className='text-center py-8 text-muted-foreground'
                    >
                      No clinics available in this agency.
                    </TableCell>
                  </TableRow>
                ) : (
                  assignments.map(row => (
                    <AssignmentRow
                      key={row.clinic_id}
                      row={row}
                      onToggleAssigned={onToggleAssigned}
                      onToggleFlag={onToggleFlag}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={isSaving}>
              {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  });
